/**
 * Geofence Form Component
 * Form for creating/editing geofences with map integration
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert as RNAlert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { GeofenceInput, Geofence } from '../../types/gps';
import gpsTrackingAPI from '../../api/services/gpsTracking';

// Web: full map editor with drawing. Mobile: "use web" message.
const GeofenceMapEditor = Platform.OS === 'web'
  ? require('./GeofenceMapEditor.web').default
  : require('./GeofenceMapEditor').default;

interface GeofenceFormProps {
  initialData?: Partial<GeofenceInput> & Partial<Geofence>;
  onSubmit: (data: GeofenceInput) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const COLORS = [
  { value: '#FF3B30', label: 'Vermelho' },
  { value: '#FF9500', label: 'Laranja' },
  { value: '#FFCC00', label: 'Amarelo' },
  { value: '#34C759', label: 'Verde' },
  { value: '#007AFF', label: 'Azul' },
  { value: '#5856D6', label: 'Roxo' },
  { value: '#FF2D55', label: 'Rosa' },
  { value: '#8E8E93', label: 'Cinza' },
];

export default function GeofenceForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
}: GeofenceFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [color, setColor] = useState(initialData?.color || '#FF3B30');
  const [geometryType, setGeometryType] = useState<'polygon' | 'circle'>(
    initialData?.geometry_type || 'polygon'
  );
  const [areaWKT, setAreaWKT] = useState(initialData?.area_wkt || '');
  const [loading, setLoading] = useState(false);
  const [geometryData, setGeometryData] = useState<any>(null);

  const handleGeometryChange = (geometry: any) => {
    setAreaWKT(geometry.wkt);
    setGeometryData(geometry);
    if (geometry?.type === 'Circle') {
      setGeometryType('circle');
    } else if (geometry?.type === 'Polygon') {
      setGeometryType('polygon');
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      RNAlert.alert('Erro', 'Nome é obrigatório');
      return;
    }

    if (!areaWKT) {
      RNAlert.alert('Erro', 'Por favor, desenhe a área no mapa');
      return;
    }

    try {
      setLoading(true);
      
      const geofenceData: any = {
        name: name.trim(),
        description: description.trim(),
        geometry_type: geometryType,
        area_wkt: areaWKT,
        color,
        is_active: true,
      };

      if (isEditing && initialData?.id) {
        await gpsTrackingAPI.geofences.updateGeofence(initialData.id, geofenceData);
        RNAlert.alert('Sucesso', 'Geofence atualizada com sucesso');
      } else {
        await gpsTrackingAPI.geofences.createGeofence(geofenceData);
        RNAlert.alert('Sucesso', 'Geofence criada com sucesso');
      }
      
      onSubmit(geofenceData);
    } catch (error: any) {
      console.error('Failed to save geofence:', error);
      RNAlert.alert('Erro', error.message || 'Falha ao guardar geofence');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
    >
      {/* Name Input */}
      <View style={styles.field}>
        <Text style={styles.label}>Nome *</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder="Ex: Centro de Lisboa"
          placeholderTextColor="#8E8E93"
        />
      </View>

      {/* Description Input */}
      <View style={styles.field}>
        <Text style={styles.label}>Descrição</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={description}
          onChangeText={setDescription}
          placeholder="Descrição opcional..."
          placeholderTextColor="#8E8E93"
          multiline
          numberOfLines={3}
        />
      </View>

      {/* Geometry Type */}
      <View style={styles.field}>
        <Text style={styles.label}>Tipo de Geometria</Text>
        <View style={styles.geometryButtons}>
          <TouchableOpacity
            style={[
              styles.geometryButton,
              geometryType === 'polygon' && styles.geometryButtonActive,
            ]}
            onPress={() => setGeometryType('polygon')}
          >
            <Ionicons
              name="triangle-outline"
              size={20}
              color={geometryType === 'polygon' ? '#FFFFFF' : '#007AFF'}
            />
            <Text
              style={[
                styles.geometryButtonText,
                geometryType === 'polygon' && styles.geometryButtonTextActive,
              ]}
            >
              Polígono
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.geometryButton,
              geometryType === 'circle' && styles.geometryButtonActive,
            ]}
            onPress={() => setGeometryType('circle')}
          >
            <Ionicons
              name="ellipse-outline"
              size={20}
              color={geometryType === 'circle' ? '#FFFFFF' : '#007AFF'}
            />
            <Text
              style={[
                styles.geometryButtonText,
                geometryType === 'circle' && styles.geometryButtonTextActive,
              ]}
            >
              Círculo
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Color Picker */}
      <View style={styles.field}>
        <Text style={styles.label}>Cor</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.colorPicker}>
            {COLORS.map((colorOption) => {
              const hex = colorOption.value;
              const isSelected = color === hex;
              return (
                <TouchableOpacity
                  key={hex}
                  style={[
                    styles.colorButton,
                    { backgroundColor: hex },
                    isSelected && styles.colorButtonActive,
                  ]}
                  onPress={() => setColor(hex)}
                >
                  <View style={styles.colorButtonInner}>
                    {isSelected ? (
                      <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                    ) : null}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>
      </View>

      {/* Map Editor: web = drawing tools, mobile = "use web" message */}
      <View style={styles.mapSection}>
        <Text style={styles.label}>Desenhar Área *</Text>
        <View style={styles.mapEditorWrapper} collapsable={false}>
          <GeofenceMapEditor
            initialGeometry={initialData?.area || initialData?.area_wkt}
            onGeometryChange={handleGeometryChange}
            geometryType={geometryType}
          />
        </View>
        {Platform.OS === 'web' && areaWKT ? (
          <View style={styles.geometrySuccess}>
            <Ionicons name="checkmark-circle" size={20} color="#34C759" />
            <Text style={styles.geometrySuccessText}>
              Área definida ({String(geometryType)})
            </Text>
          </View>
        ) : null}
      </View>

      {/* Action Buttons */}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Cancelar</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[
            styles.submitButton,
            (loading || !areaWKT) && styles.submitButtonDisabled
          ]} 
          onPress={handleSubmit}
          disabled={loading || !areaWKT}
        >
          {loading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.submitButtonText}>
              {isEditing ? 'Atualizar' : 'Criar'} Geofence
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 24,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#F2F2F7',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: '#000000',
    borderWidth: 1,
    borderColor: '#E5E5EA',
    ...Platform.select({
      web: {
        outlineWidth: 0,
      },
    }),
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  geometryButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  geometryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  geometryButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  geometryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
  geometryButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  colorPicker: {
    flexDirection: 'row',
    gap: 12,
  },
  colorButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  colorButtonInner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorButtonActive: {
    borderColor: '#000000',
    borderWidth: 3,
  },
  infoBox: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: '#E5F1FF',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: '#007AFF',
    lineHeight: 18,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 20,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8E8E93',
  },
  submitButton: {
    flex: 2,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: '#007AFF',
    alignItems: 'center',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  submitButtonDisabled: {
    backgroundColor: '#C7C7CC',
  },
  mapSection: {
    marginBottom: 20,
  },
  mapEditorWrapper: {
    flex: 1,
    minHeight: 1,
  },
  geometrySuccess: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    padding: 12,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
  },
  geometrySuccessText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#34C759',
  },
});
